import { useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, MapPin, Search } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { scrollToId } from "@/lib/scroll";
import { useSettings } from "@/context/SettingsContext";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const HeroSection = () => {
  const settings = useSettings();
  const travellersNum = Number(settings.stat_travellers) || 0;
  const travellersLabel = travellersNum >= 1000 ? `${Math.round(travellersNum / 1000)}K+` : `${travellersNum}+`;
  const stats = [
    { value: `${settings.stat_years}+`, label: "Years of Craft" },
    { value: `${settings.stat_countries}+`, label: "Countries" },
    { value: travellersLabel, label: "Happy Travellers" },
    { value: settings.stat_visa_success, label: "Visa Success" },
  ];
  const ref = useRef(null);
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [allPackages, setAllPackages] = useState([]);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    api.get("/packages").then((res) => setAllPackages(res.data)).catch(() => {});
  }, []);

  const q = query.trim().toLowerCase();
  const matches = q
    ? allPackages
        .filter((p) =>
          [p.title, p.destination, p.continent, p.tour_type, ...(p.states || []), ...(p.countries || []), ...(p.themes || [])]
            .filter(Boolean)
            .some((v) => v.toLowerCase().includes(q))
        )
        .slice(0, 6)
    : [];

  const submitSearch = (e) => {
    e.preventDefault();
    if (!q) return;
    // route to the category with the most matches; tours always outrank visas
    const counts = { international: 0, domestic: 0, visa: 0 };
    allPackages
      .filter((p) =>
        [p.title, p.destination, p.continent, p.tour_type, ...(p.states || []), ...(p.countries || []), ...(p.themes || [])]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(q))
      )
      .forEach((p) => { counts[p.category] = (counts[p.category] || 0) + 1; });
    const best = Object.entries(counts).sort((a, b) => b[1] - a[1]).find(([, n]) => n > 0);
    navigate(`/packages/${best ? best[0] : "international"}?q=${encodeURIComponent(query.trim())}`);
  };

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-screen min-h-[720px] overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: 1.15 }}>
        <img
          src="https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&w=2000&q=85"
          alt="Emerald alpine lake at dawn"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/55 to-ink" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(5,26,24,0.55)_100%)]" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 flex flex-col justify-center"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="font-mono text-[11px] sm:text-xs tracking-[0.35em] uppercase text-gold mb-6"
        >
          Domestic · International · Visa Concierge
        </motion.p>

        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-cream max-w-3xl">
          <MaskedLine delay={0.3}>Crafted journeys.</MaskedLine>
          <MaskedLine delay={0.45}>
            <span className="italic gold-text-gradient">Timeless memories.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-mist leading-relaxed"
        >
          From Rajasthan's golden palaces to overwater villas in the Maldives —
          hand-built itineraries, fast-track visas and a concierge that travels with you.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollToId("packages")}
            data-testid="hero-explore-btn"
            className="group bg-gold text-ink font-semibold text-sm px-8 py-4 rounded-full hover:bg-gold-hover transition-colors duration-200 flex items-center gap-2"
          >
            Explore Packages
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
          </button>
          <button
            onClick={() => scrollToId("enquiry")}
            data-testid="hero-plan-trip-btn"
            className="border border-cream/30 text-cream text-sm px-8 py-4 rounded-full hover:border-gold hover:text-gold transition-colors duration-200"
          >
            Plan My Trip
          </button>
        </motion.div>

        <motion.form
          onSubmit={submitSearch}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.25 }}
          className="mt-6 max-w-xl relative"
          data-testid="hero-search-form"
        >
          <div className="flex items-center bg-card/70 backdrop-blur-md border border-gold/25 rounded-full pl-5 pr-1.5 py-1.5">
            <Search size={17} className="text-gold shrink-0" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setTimeout(() => setFocused(false), 180)}
              data-testid="hero-search-input"
              className="flex-1 bg-transparent outline-none px-3 text-sm text-cream placeholder:text-faded"
              placeholder="Search destinations — Kashmir, Europe, Dubai, Thailand…"
            />
            <button type="submit" data-testid="hero-search-btn" className="bg-gold text-ink text-xs font-semibold px-5 py-2.5 rounded-full hover:bg-gold-hover transition-colors">
              Search
            </button>
          </div>
          {focused && matches.length > 0 && (
            <div className="absolute left-0 right-0 top-full mt-2 rounded-2xl border border-gold/25 bg-surface/95 backdrop-blur-xl overflow-hidden shadow-2xl z-30" data-testid="hero-search-results">
              {matches.map((m) => (
                <Link key={m.id} to={`/package/${m.id}`} data-testid={`hero-search-result-${m.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-gold/10 transition-colors border-b border-cream/5 last:border-0">
                  <img src={m.image} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-cream truncate">{m.title}</span>
                    <span className="block text-[11px] text-faded truncate">{m.destination}</span>
                  </span>
                  <span className="text-xs font-display text-gold">{formatINR(m.price)}</span>
                </Link>
              ))}
            </div>
          )}
        </motion.form>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.35 }}
          className="absolute bottom-10 left-4 right-4 sm:left-8 sm:right-8 lg:left-16 lg:right-16 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-cream/15 pt-6"
        >
          {stats.map((s) => (
            <div key={s.label} data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <p className="font-display text-2xl sm:text-3xl text-gold">{s.value}</p>
              <p className="text-xs tracking-[0.2em] uppercase text-mist mt-1">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollToId("collections")}
        data-testid="hero-scroll-down-btn"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 8, 0] }}
        transition={{ opacity: { delay: 1.8, duration: 0.8 }, y: { repeat: Infinity, duration: 2, ease: "easeInOut" } }}
        className="absolute bottom-40 md:bottom-10 right-6 md:right-10 z-10 text-gold hidden sm:block"
        aria-label="Scroll down"
      >
        <ArrowDown size={22} />
      </motion.button>
    </section>
  );
};
